import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { loadStripe, type Appearance, type Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./stripeConnectForm";
import Info from "../info";
import type { BookingData } from "../info";
import PaymentError from "../paymentError";
import { useGuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";
import { useWidgetColourScheme } from "../hooks/useWidgetColourScheme";
import { buildWidgetStripeAppearance, MULISH_FONT_CSS_SRC } from "../../utils/stripeAppearance";
import * as rpc from "../../../common/jsonRpcRoutes";
import { isAbortedRequest, useRpcCaller } from "../context/WidgetRequestContext";

interface DietaryRequirement {
  diet_types?: string[];
  allergies?: string[];
  name?: string;
  notes?: string;
}

interface StripeConnectProps {
  bookingId?: string;
  pendingBookingId?: string;
  amount?: number;
  bookingData?: {
    depositTotal: number;
    guestCount: number;
    time: string;
    date: string;
    bookingId: string;
    bookingRef: string;
    venue: {
      title: string;
      currency: string;
      country_code: string | null;
    };
    paymentProcessor: string;
    depositTypePayment: string | boolean; // revert to string for all downstream consumers
    /** The venue's deposit terms, repeated on the confirmation. */
    depositTerms?: string;
    deposits?: string[]; // Add this property to fix the error
  };
  showInfo?: boolean;
  action?: string;
  pendingPreorderId?: string;
  name?: string;
  email?: string;
  customerNotes?: string;
  dietaryRequirements?: DietaryRequirement[];
  vouchers?: Array<{ code: string; amountToRedeem: number }>;
  /**
   * When true the booking type has credit card payments disabled, so the form
   * refuses a credit-funded card before confirming the payment.
   */
  disableCreditCards?: boolean;
  /**
   * Stripe always uses `redirect: "always"` for confirmPayment/confirmSetup,
   * navigating the browser to `/confirm/` after payment. The original page
   * is destroyed, so an in-page success callback cannot fire.
   * Post-payment completion is handled by the ConfirmPayment component which
   * polls `/checkDeposit`. FreedomPay uses in-page callbacks instead — see
   * FreedomPay's `onPaymentSuccess` prop.
   */
  mode?: "full" | "successOnly" | "successWithDetails";
}

// Payload type for /api/stripe/initialise
interface InitialisePayload {
  booking_id?: string;
  pending_booking_id?: string;
  action: "place-your-order" | "manage-your-order" | "secure-booking" | "book";
  pending_preorder_id?: string;
  deposit_ids?: string[];
  name?: string;
  email?: string;
  customer_notes?: string;
  dietary_requirements?: DietaryRequirement[];
  vouchers?: Array<{ code: string; amountToRedeem: number }>;
}

function getBookingIdField(
  pendingBookingId?: string,
  bookingId?: string,
): Pick<InitialisePayload, "pending_booking_id" | "booking_id"> {
  if (pendingBookingId) return { pending_booking_id: pendingBookingId };
  if (bookingId) return { booking_id: bookingId };
  return {};
}

function buildBasePayload(
  action: string | undefined,
  bookingId: string | undefined,
  pendingBookingId: string | undefined,
  pendingPreorderId: string | undefined,
): InitialisePayload | null {
  if (action === "place-your-order" || action === "manage-your-order") {
    if (!bookingId) return null;
    return { booking_id: bookingId, action, pending_preorder_id: pendingPreorderId };
  }
  const resolvedAction: "book" | "secure-booking" = action === "book" ? "book" : "secure-booking";
  return { action: resolvedAction, ...getBookingIdField(pendingBookingId, bookingId) };
}

function buildInitialisePayload(
  action: string | undefined,
  bookingId: string | undefined,
  pendingBookingId: string | undefined,
  pendingPreorderId: string | undefined,
  deposits: string[] | undefined,
  refs: {
    name?: string;
    email?: string;
    customerNotes?: string;
    dietaryRequirements?: DietaryRequirement[];
    vouchers?: Array<{ code: string; amountToRedeem: number }>;
  },
): InitialisePayload | null {
  if (!bookingId && !pendingBookingId) return null;
  const payload = buildBasePayload(action, bookingId, pendingBookingId, pendingPreorderId);
  if (!payload) return null;

  if (deposits?.length) payload.deposit_ids = deposits;
  if (refs.name) payload.name = refs.name;
  if (refs.email) payload.email = refs.email;
  if (refs.customerNotes) payload.customer_notes = refs.customerNotes;
  if (refs.dietaryRequirements?.length) payload.dietary_requirements = refs.dietaryRequirements;
  if (refs.vouchers?.length) payload.vouchers = refs.vouchers;

  return payload;
}

// For Info, normalize bookingData to match Info's BookingData type
function normalizeInfoBookingData(bookingData: StripeConnectProps["bookingData"]): BookingData | undefined {
  if (!bookingData || typeof bookingData.venue.title !== "string") {
    return bookingData as unknown as BookingData;
  }
  return {
    ...bookingData,
    venue: {
      title: bookingData.venue.title,
      currency: bookingData.venue.currency,
      country_code: bookingData.venue.country_code,
    },
    depositTypePayment: bookingData.depositTypePayment === "true",
  };
}

// For CheckoutForm, ensure depositTypePayment is a string
function normalizeCheckoutBookingData(bookingData: StripeConnectProps["bookingData"]) {
  if (!bookingData || typeof bookingData.depositTypePayment !== "boolean") return bookingData;
  return { ...bookingData, depositTypePayment: bookingData.depositTypePayment ? "true" : "false" };
}

const StripeConnect: React.FC<StripeConnectProps> = ({
  bookingId,
  pendingBookingId,
  bookingData,
  showInfo,
  action,
  amount,
  pendingPreorderId,
  name,
  email,
  customerNotes,
  dietaryRequirements,
  vouchers,
  disableCreditCards,
  mode,
}) => {
  const call = useRpcCaller();
  const { t } = useTranslation();
  const { design, theme, venueStyleKey } = useGuestCheckoutSettings();
  // The design's own payment fields, which is independent of `isWidget` — that
  // also changes the shared components' markup.
  const usesDesign = design === "current";
  // On a legacy surface "light" is passed so it never subscribes to the media
  // query (the hook only listens for "auto") — its behaviour is unchanged.
  const colourScheme = useWidgetColourScheme(usesDesign ? theme : "light");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus] = useState<"idle" | "success" | "error">("idle");
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [elementsAppearance, setElementsAppearance] = useState<Appearance | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const nameRef = useRef(name);
  const emailRef = useRef(email);
  const customerNotesRef = useRef(customerNotes);
  const dietaryRequirementsRef = useRef(dietaryRequirements);
  const vouchersRef = useRef(vouchers);

  useEffect(() => {
    nameRef.current = name;
    emailRef.current = email;
    customerNotesRef.current = customerNotes;
    dietaryRequirementsRef.current = dietaryRequirements;
    vouchersRef.current = vouchers;
  }, [name, email, customerNotes, dietaryRequirements, vouchers]);

  useEffect(() => {
    call("/stripe-public-key", {})
      .then((data) => {
        setStripePromise(loadStripe(data.publicKey));
      })
      .catch((err) => {
        if (isAbortedRequest(err)) return;
        setError(t("failed_load_payment_config", "Failed to load payment configuration."));
      });
  }, [call, t]);

  const [vouchersChanged, setVouchersChanged] = useState(0);
  const isInitialMount = useRef(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to trigger payment intent re-initialization when vouchers change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setVouchersChanged((prev) => prev + 1);
  }, [vouchers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: vouchersChanged is used to trigger re-initialization when vouchers change
  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        const payload = buildInitialisePayload(
          action,
          bookingId,
          pendingBookingId,
          pendingPreorderId,
          bookingData?.deposits,
          {
            name: nameRef.current,
            email: emailRef.current,
            customerNotes: customerNotesRef.current,
            dietaryRequirements: dietaryRequirementsRef.current,
            vouchers: vouchersRef.current,
          },
        );
        if (!payload) return;

        const data = await call("/api/stripe/initialise", payload);
        setClientSecret(data.payment_intent.client_secret);
      } catch (err) {
        if (isAbortedRequest(err)) return;
        setError(t("failed_initialize_payment", "Failed to initialise payment. Please try again."));
      }
    };

    fetchClientSecret();
  }, [bookingId, pendingBookingId, t, action, pendingPreorderId, bookingData?.deposits, vouchersChanged]);

  const infoBookingData = normalizeInfoBookingData(bookingData);
  const checkoutBookingData = normalizeCheckoutBookingData(bookingData);

  // The appearance is a snapshot of computed values, so it has to be rebuilt
  // whenever those change: on a live scheme flip, and when the venue's
  // overrides change while mounted. Both are written to the DOM in the same
  // commit, and layout effects run after the mutations, so getComputedStyle
  // sees the new values. react-stripe-js forwards the new appearance to
  // elements.update(), so the fields repaint without remounting.
  // biome-ignore lint/correctness/useExhaustiveDependencies: venueStyleKey is not read here — it marks when the computed values the snapshot is built from have changed
  useLayoutEffect(() => {
    if (usesDesign) {
      setElementsAppearance(buildWidgetStripeAppearance(wrapperRef.current, colourScheme));
    }
  }, [usesDesign, colourScheme, venueStyleKey]);

  return (
    <div ref={wrapperRef} className="gcw:w-full">
      {paymentStatus !== "success" && showInfo && <Info bookingData={infoBookingData} amount={amount} />}

      {error && <PaymentError errorMessages={[error]} />}

      {clientSecret && stripePromise ? (
        <Elements
          key={clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret,
            // react-stripe-js forwards a changed options.appearance to
            // elements.update({ appearance }), so a live scheme flip repaints
            // without remounting and typed card details survive.
            appearance: elementsAppearance ?? {},
            ...(usesDesign ? { fonts: [{ cssSrc: MULISH_FONT_CSS_SRC }] } : {}),
            // @ts-expect-error: paymentMethodCreation is not in StripeElementsOptions, but required for manual confirmation in some flows
            paymentMethodCreation: "manual",
          }}
        >
          <CheckoutForm
            paymentStatus={paymentStatus}
            bookingData={checkoutBookingData}
            clientSecret={clientSecret}
            mode={mode}
            amount={amount}
            action={action}
            pendingBookingId={pendingBookingId}
            bookingId={bookingId}
            disableCreditCards={disableCreditCards}
          />
        </Elements>
      ) : (
        <div className="loading-spinner"></div>
      )}
    </div>
  );
};

export default StripeConnect;
